import React from "react";
import {
  TrashIcon,
  PencilSquareIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { AccessCodeI } from "../../types";
import { useGetAccessCodeAnalyticsByCode } from "../../api/queries";
import Button from "../../componentLibrary/Button";
import Modal from "../../componentLibrary/Modal";
import Spinner from "../../componentLibrary/Spinner";
import { useNotification } from "../../hooks/useNotification";

interface AccessCodeDetailsModalProps {
  accessCode: AccessCodeI;
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const AccessCodeDetailsModal: React.FC<AccessCodeDetailsModalProps> = ({
  accessCode,
  isOpen,
  onClose,
  onEdit,
  onDelete,
}) => {
  const { notify } = useNotification();
  const {
    data: analytics,
    isLoading,
    error,
  } = useGetAccessCodeAnalyticsByCode(accessCode.accessCode);

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (date: Date | string | undefined) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCopyLink = () => {
    const baseUrl = window.location.origin;
    const signupLink = `${baseUrl}/signup?code=${encodeURIComponent(accessCode.accessCode)}`;
    navigator.clipboard
      .writeText(signupLink)
      .then(() => {
        notify("success", "Signup link copied to clipboard!");
      })
      .catch(() => {
        notify("error", "Failed to copy link to clipboard");
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onCloseModal={onClose}
      title="Access Code Details & Analytics"
      size="lg"
      footer={
        <div className="flex justify-between w-full">
          <Button
            onClick={onDelete}
            variant="cautionary"
            className="flex items-center gap-2"
          >
            <TrashIcon className="w-5 h-5" />
            Delete
          </Button>
          <div className="flex gap-3">
            <Button onClick={onClose} variant="secondary">
              Close
            </Button>
            <Button
              onClick={onEdit}
              variant="dark"
              className="flex items-center gap-2"
            >
              <PencilSquareIcon className="w-5 h-5" />
              Edit
            </Button>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Spinner className="w-12 h-12" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600 font-medium">
            Failed to load analytics data. Please try again.
          </p>
        </div>
      ) : analytics ? (
        <div className="space-y-6">
          {/* Code Information Section */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Code Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Access Code
                </label>
                <p className="text-gray-900 font-mono font-bold text-lg">
                  {analytics.accessCode.code}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Segment
                </label>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                  {analytics.accessCode.segment}
                </span>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Status
                </label>
                <div className="flex items-center gap-2">
                  {analytics.accessCode.isActive ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      <span className="text-green-700 font-semibold">
                        Active
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-700 font-semibold">
                        Inactive
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Expiration
                </label>
                <div className="flex items-center gap-2">
                  {analytics.accessCode.isExpired ? (
                    <>
                      <ClockIcon className="w-5 h-5 text-red-500" />
                      <span className="text-red-700 font-semibold">
                        Expired
                      </span>
                    </>
                  ) : analytics.accessCode.expirationDate ? (
                    <>
                      <ClockIcon className="w-5 h-5 text-blue-500" />
                      <span className="text-gray-900">
                        {formatDate(analytics.accessCode.expirationDate)}
                        {analytics.accessCode.daysUntilExpiration !== null &&
                          ` (${analytics.accessCode.daysUntilExpiration} days)`}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-600">No expiration</span>
                  )}
                </div>
              </div>
            </div>

            {/* Copy Signup Link Button */}
            <div className="mt-4 pt-4 border-t border-gray-300">
              <Button
                onClick={handleCopyLink}
                variant="dark"
                className="w-full flex items-center justify-center gap-2"
              >
                <LinkIcon className="w-5 h-5" />
                Copy Signup Link
              </Button>
            </div>
          </div>

          {/* Usage Statistics Section */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Usage Statistics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Total Redemptions
                </label>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.redemptionStats.totalRedemptions}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Limit
                </label>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.accessCode.limitCount || "Unlimited"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Remaining
                </label>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.redemptionStats.remainingRedemptions !== null
                    ? analytics.redemptionStats.remainingRedemptions
                    : "∞"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Usage %
                </label>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.redemptionStats.percentageUsed !== null
                    ? `${analytics.redemptionStats.percentageUsed}%`
                    : "N/A"}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            {analytics.accessCode.limitCount && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      analytics.redemptionStats.percentageUsed! >= 100
                        ? "bg-red-500"
                        : analytics.redemptionStats.percentageUsed! >= 80
                          ? "bg-yellow-500"
                          : "bg-green-500"
                    }`}
                    style={{
                      width: `${Math.min(
                        analytics.redemptionStats.percentageUsed || 0,
                        100,
                      )}%`,
                    }}
                  ></div>
                </div>
                {analytics.redemptionStats.limitReached && (
                  <p className="text-sm text-red-600 font-semibold mt-2">
                    ⚠️ Redemption limit reached
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Users Who Redeemed Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <UserIcon className="w-6 h-6 text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Users Who Redeemed ({analytics.users.length})
              </h3>
            </div>

            {analytics.users.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">
                  No users have redeemed this code yet
                </p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Username
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Redeemed At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analytics.users.map((user, index) => (
                      <tr
                        key={user.userSub}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.username}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {user.email}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {formatDateTime(user.redeemedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Metadata Section */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1">
                Created At
              </label>
              <p className="text-sm text-gray-900">
                {formatDateTime(analytics.accessCode.createdAt)}
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1">
                Last Updated
              </label>
              <p className="text-sm text-gray-900">
                {formatDateTime(analytics.accessCode.updatedAt)}
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default AccessCodeDetailsModal;
